import { useEffect, useState } from "react";
import { BadgeAlert, BadgeCheck, BadgeX, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", category: "bar", rate: "", lead_time_days: 7, contact_name: "", email: "", phone: "", offering: "" };

const COMPLIANCE = {
  verified: { label: "Verified", cls: "border-emerald-600 bg-emerald-50 text-emerald-700", Icon: BadgeCheck },
  pending: { label: "Pending", cls: "border-amber-500 bg-amber-50 text-amber-700", Icon: BadgeAlert },
  expired: { label: "Expired", cls: "border-red-600 bg-red-50 text-red-700", Icon: BadgeX },
};

export default function Providers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/providers").then((r) => setItems(r.data.filter((p) => !p.archived))).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, category: p.category, rate: p.rate, lead_time_days: p.lead_time_days, contact_name: p.contact_name || "", email: p.email || "", phone: p.phone || "", offering: p.offering || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, rate: Number(form.rate || 0), lead_time_days: Number(form.lead_time_days || 0) };
    try {
      if (editing) await api.patch(`/providers/${editing.id}`, payload);
      else await api.post("/providers", payload);
      toast.success(editing ? "Provider updated" : "Provider added to directory");
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name} permanently?`)) return;
    try {
      await api.delete(`/providers/${p.id}`);
      toast.success("Provider deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cycleCompliance = async (p) => {
    const order = ["pending", "verified", "expired"];
    const next = order[(order.indexOf(p.compliance_status) + 1) % order.length];
    try {
      await api.patch(`/providers/${p.id}`, { compliance_status: next });
      toast.success(`${p.name} compliance → ${next}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="providers-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Outside Vendors</h1>
          <p className="mt-1 text-sm text-muted-foreground">Directory of external units &amp; services you can call on — contact them manually for availability.</p>
        </div>
        <Button data-testid="new-provider-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> Add Vendor
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => {
          const c = COMPLIANCE[p.compliance_status] || COMPLIANCE.pending;
          return (
            <div key={p.id} data-testid={`provider-card-${p.id}`} className="border border-border bg-white p-5 transition-transform duration-200 hover:-translate-y-[2px]">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-base font-bold tracking-tight">{p.name}</h3>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground capitalize">{p.category}</p>
                </div>
                <button data-testid={`compliance-badge-${p.id}`} onClick={() => cycleCompliance(p)} title="Click to update compliance status"
                  className={`flex items-center gap-1.5 border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors duration-150 ${c.cls}`}>
                  <c.Icon className="h-3.5 w-3.5" /> {c.label}
                </button>
              </div>
              {p.offering && <p className="mt-3 border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid={`provider-offering-${p.id}`}>{p.offering}</p>}
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-muted-foreground">Day rate</dt><dd className="font-bold tabular-nums" data-testid={`provider-rate-${p.id}`}>{fmt(p.rate)}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Lead time</dt><dd className="font-medium tabular-nums">{p.lead_time_days} days</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Contact</dt><dd className="font-medium">{p.contact_name || "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd className="font-medium">{p.email || "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Phone</dt><dd className="font-medium">{p.phone || "—"}</dd></div>
              </dl>
              <div className="mt-4 flex gap-2 border-t border-border pt-4">
                <Button data-testid={`edit-provider-${p.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => openEdit(p)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button data-testid={`delete-provider-${p.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => remove(p)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="col-span-3 border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">No outside vendors in the directory yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="provider-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Vendor" : "Add Outside Vendor"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Name</Label><Input data-testid="provider-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="provider-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="bar">Bar Services</SelectItem>
                  <SelectItem value="marquee">Marquee / Structures</SelectItem>
                  <SelectItem value="staffing">Waiting Staff</SelectItem>
                  <SelectItem value="equipment">Equipment Hire</SelectItem>
                  <SelectItem value="unit">Extra Van / Unit</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Day rate (£)</Label><Input data-testid="provider-rate-input" type="number" min="0" required value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>What they can supply</Label><Input data-testid="provider-offering-input" placeholder="e.g. 2× pizza vans, mobile bar trailer…" value={form.offering} onChange={(e) => setForm({ ...form, offering: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Lead time (days)</Label><Input data-testid="provider-leadtime-input" type="number" min="0" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Contact name</Label><Input data-testid="provider-contact-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="provider-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="provider-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2"><Button data-testid="provider-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editing ? "Save Changes" : "Add Vendor"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
